import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { SettingsContext } from '../contexts/SettingsContext';
import LogoUploader from '../components/LogoUploader';
import SponsorManager from '../components/SponsorManager';
import { useLanguage } from '../contexts/LanguageContext';
import { usePlan } from '../contexts/PlanContext';

/**
 * SystemSettings - Admin-Seite für Systemeinstellungen
 *
 * Ermöglicht die Konfiguration von:
 * - App-Name (wird als Browser-Titel verwendet)
 * - Haupt-Logo (Header + /present Footer)
 * - Sponsor-Logo + Anzeige-Toggle
 * - Theme-Modus (Auto/Force Light/Force Dark)
 */
function SystemSettings() {
  const { t } = useLanguage();
  const { refreshSettings } = useContext(SettingsContext);
  const { limits } = usePlan();

  // Lokaler State für Formular
  const [formData, setFormData] = useState({
    appName: '',
    showSponsor: true,
    themeMode: 'auto',
    slideDuration: 10,
    sponsorRotationDuration: 5,
    qrUrl: ''
  });

  // UI State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Raw settings für Logo-URLs
  const [rawSettings, setRawSettings] = useState({});

  // Settings laden
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/system-settings-admin');

      // Settings in Map umwandeln
      const settingsMap = {};
      response.data.forEach(setting => {
        settingsMap[setting.key] = setting.value;
      });

      setRawSettings(settingsMap);

      // Formular-State setzen
      setFormData({
        appName: settingsMap.app_name || 'Padel Turnier',
        showSponsor: settingsMap.show_sponsor === 'true',
        themeMode: settingsMap.theme_mode || 'auto',
        slideDuration: parseInt(settingsMap.slide_duration) || 10,
        sponsorRotationDuration: parseInt(settingsMap.sponsor_rotation_duration) || 5,
        qrUrl: settingsMap.qr_url || ''
      });

    } catch (err) {
      console.error('Error loading settings:', err);
      setError(t('systemSettings.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Änderungen erkennen
  useEffect(() => {
    const changed =
      formData.appName !== (rawSettings.app_name || 'Padel Turnier') ||
      formData.showSponsor !== (rawSettings.show_sponsor === 'true') ||
      formData.themeMode !== (rawSettings.theme_mode || 'auto') ||
      formData.slideDuration !== (parseInt(rawSettings.slide_duration) || 10) ||
      formData.sponsorRotationDuration !== (parseInt(rawSettings.sponsor_rotation_duration) || 5) ||
      formData.qrUrl !== (rawSettings.qr_url || '');

    setHasChanges(changed);
  }, [formData, rawSettings]);

  // Einzelnes Setting speichern (upsert: PUT wenn vorhanden, POST wenn neu)
  const saveSetting = async (key, value) => {
    try {
      await axios.put(
        `/api/system-settings/${key}?value=${encodeURIComponent(value)}`,
        {}
      );
    } catch (err) {
      if (err.response?.status === 404) {
        await axios.post(
          `/api/system-settings?key=${encodeURIComponent(key)}&value=${encodeURIComponent(value)}&category=branding`,
          {}
        );
      } else {
        throw err;
      }
    }
  };

  // Alle Änderungen speichern
  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Geänderte Settings speichern
      if (formData.appName !== rawSettings.app_name) {
        await saveSetting('app_name', formData.appName);
      }

      if (formData.showSponsor !== (rawSettings.show_sponsor === 'true')) {
        await saveSetting('show_sponsor', formData.showSponsor.toString());
      }

      if (formData.themeMode !== rawSettings.theme_mode) {
        await saveSetting('theme_mode', formData.themeMode);
      }

      if (formData.slideDuration !== (parseInt(rawSettings.slide_duration) || 10)) {
        await saveSetting('slide_duration', formData.slideDuration.toString());
      }

      if (formData.sponsorRotationDuration !== (parseInt(rawSettings.sponsor_rotation_duration) || 5)) {
        await saveSetting('sponsor_rotation_duration', formData.sponsorRotationDuration.toString());
      }

      if (formData.qrUrl !== (rawSettings.qr_url || '')) {
        await saveSetting('qr_url', formData.qrUrl);
      }

      setSuccess(t('systemSettings.saveSuccess'));
      setTimeout(() => setSuccess(''), 3000);

      // Settings neu laden
      await loadSettings();

      // Globalen Context aktualisieren
      refreshSettings();

    } catch (err) {
      console.error('Error saving settings:', err);
      setError(t('systemSettings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // Logo-Upload Handler
  const handleLogoUpload = () => {
    // Nach Upload neu laden
    loadSettings();
    refreshSettings();
  };

  // Formular-Änderungen
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-md">
          <p className="text-slate-500">{t('systemSettings.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-md">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          {t('systemSettings.title')}
        </h1>

        <p className="text-slate-500 dark:text-slate-400 mb-8">
          {t('systemSettings.subtitle')}
        </p>

        {/* Fehlermeldung */}
        {error && (
          <div className="px-4 py-3 rounded-lg mb-6 text-sm bg-red-500/10 border border-red-500 text-red-500">
            {error}
          </div>
        )}

        {/* Erfolgsmeldung */}
        {success && (
          <div className="px-4 py-3 rounded-lg mb-6 text-sm bg-green-500/10 border border-green-500 text-green-500">
            {success}
          </div>
        )}

        {/* Section: General */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4 pb-2 border-b-2 border-slate-200 dark:border-slate-700">
            {t('systemSettings.general')}
          </h2>

          <div className="mb-6 p-5 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
            <label className="block font-semibold text-base text-slate-900 dark:text-white mb-1" htmlFor="appName">
              {t('systemSettings.appName')}
            </label>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              {t('systemSettings.appNameHint')}
            </p>
            <input
              type="text"
              id="appName"
              className="w-full max-w-md px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-base transition-colors focus:outline-none focus:border-gold"
              value={formData.appName}
              onChange={(e) => handleInputChange('appName', e.target.value)}
              placeholder={t('systemSettings.appNamePlaceholder')}
            />
          </div>
        </section>

        {/* Section: Branding */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4 pb-2 border-b-2 border-slate-200 dark:border-slate-700">
            {t('systemSettings.branding')}
          </h2>

          {limits && !limits.custom_logo ? (
            <div style={{
              padding: '12px 16px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 14,
              color: 'var(--text-secondary)',
            }}>
              🔒 Eigenes Logo ist ab dem <strong>Club-Plan</strong> verfügbar.
            </div>
          ) : (
            <LogoUploader
              label={t('systemSettings.mainLogo')}
              settingKey="logo_url"
              currentValue={rawSettings.logo_url}
              onUploadSuccess={handleLogoUpload}
              helpText={t('systemSettings.mainLogoHint')}
            />
          )}

          <div className="mb-6 p-5 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
            <label className="block font-semibold text-base text-slate-900 dark:text-white mb-1">
              {t('systemSettings.sponsors')}
            </label>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              {t('systemSettings.sponsorsHint')}
            </p>
            <SponsorManager onUpdate={() => { loadSettings(); refreshSettings(); }} />
          </div>

          <div className="mb-6 p-5 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
            <label className="block font-semibold text-base text-slate-900 dark:text-white mb-1">
              {t('systemSettings.showSponsor')}
            </label>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              {t('systemSettings.showSponsorHint')}
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="relative inline-block w-[52px] h-7 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.showSponsor}
                  onChange={(e) => handleInputChange('showSponsor', e.target.checked)}
                  className="sr-only peer"
                />
                <span className="absolute inset-0 bg-slate-300 dark:bg-slate-600 rounded-full transition-colors peer-checked:bg-green-500"></span>
                <span className="absolute left-[3px] bottom-[3px] w-[22px] h-[22px] bg-white rounded-full transition-transform peer-checked:translate-x-6"></span>
              </label>
              <span className="text-slate-500 dark:text-slate-400 text-sm">
                {formData.showSponsor ? t('systemSettings.sponsorVisible') : t('systemSettings.sponsorHidden')}
              </span>
            </div>
          </div>

          <div className="mb-6 p-5 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
            <label className="block font-semibold text-base text-slate-900 dark:text-white mb-1" htmlFor="sponsorRotation">
              {t('systemSettings.sponsorRotation')}
            </label>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              {t('systemSettings.sponsorRotationHint')}
            </p>
            <div className="flex items-center gap-3">
              <input
                type="number"
                id="sponsorRotation"
                min="1"
                max="60"
                className="w-28 px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-base transition-colors focus:outline-none focus:border-gold"
                value={formData.sponsorRotationDuration}
                onChange={(e) => handleInputChange('sponsorRotationDuration', Math.max(1, Math.min(60, parseInt(e.target.value) || 5)))}
              />
              <span className="text-slate-500 dark:text-slate-400 text-sm">{t('systemSettings.seconds160')}</span>
            </div>
          </div>
        </section>

        {/* Section: Appearance */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4 pb-2 border-b-2 border-slate-200 dark:border-slate-700">
            {t('systemSettings.appearance')}
          </h2>

          <div className="mb-6 p-5 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
            <label className="block font-semibold text-base text-slate-900 dark:text-white mb-1">
              {t('systemSettings.themeMode')}
            </label>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              {t('systemSettings.themeModeHint')}
            </p>

            <div className="flex flex-col gap-3">
              <label className="flex items-start gap-3 p-4 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg cursor-pointer transition-colors hover:border-gold">
                <input
                  type="radio"
                  name="themeMode"
                  value="auto"
                  checked={formData.themeMode === 'auto'}
                  onChange={(e) => handleInputChange('themeMode', e.target.value)}
                  className="mt-1 w-[18px] h-[18px] accent-gold"
                />
                <span className="flex flex-col gap-1">
                  <strong className="text-slate-900 dark:text-white">{t('systemSettings.auto')}</strong>
                  <small className="text-slate-500 dark:text-slate-400 text-sm">{t('systemSettings.autoHint')}</small>
                </span>
              </label>

              <label className="flex items-start gap-3 p-4 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg cursor-pointer transition-colors hover:border-gold">
                <input
                  type="radio"
                  name="themeMode"
                  value="force_light"
                  checked={formData.themeMode === 'force_light'}
                  onChange={(e) => handleInputChange('themeMode', e.target.value)}
                  className="mt-1 w-[18px] h-[18px] accent-gold"
                />
                <span className="flex flex-col gap-1">
                  <strong className="text-slate-900 dark:text-white">{t('systemSettings.forceLight')}</strong>
                  <small className="text-slate-500 dark:text-slate-400 text-sm">{t('systemSettings.forceLightHint')}</small>
                </span>
              </label>

              <label className="flex items-start gap-3 p-4 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg cursor-pointer transition-colors hover:border-gold">
                <input
                  type="radio"
                  name="themeMode"
                  value="force_dark"
                  checked={formData.themeMode === 'force_dark'}
                  onChange={(e) => handleInputChange('themeMode', e.target.value)}
                  className="mt-1 w-[18px] h-[18px] accent-gold"
                />
                <span className="flex flex-col gap-1">
                  <strong className="text-slate-900 dark:text-white">{t('systemSettings.forceDark')}</strong>
                  <small className="text-slate-500 dark:text-slate-400 text-sm">{t('systemSettings.forceDarkHint')}</small>
                </span>
              </label>
            </div>
          </div>
        </section>

        {/* Section: Presentation View */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4 pb-2 border-b-2 border-slate-200 dark:border-slate-700">
            {t('systemSettings.presentationSection')}
          </h2>

          <div className="mb-6 p-5 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
            <label className="block font-semibold text-base text-slate-900 dark:text-white mb-1" htmlFor="slideDuration">
              {t('systemSettings.slideDuration')}
            </label>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              {t('systemSettings.slideDurationHint')}
            </p>
            <div className="flex items-center gap-3">
              <input
                type="number"
                id="slideDuration"
                min="3"
                max="120"
                className="w-28 px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-base transition-colors focus:outline-none focus:border-gold"
                value={formData.slideDuration}
                onChange={(e) => handleInputChange('slideDuration', Math.max(3, Math.min(120, parseInt(e.target.value) || 10)))}
              />
              <span className="text-slate-500 dark:text-slate-400 text-sm">{t('systemSettings.seconds3120')}</span>
            </div>
          </div>

          <div className="mb-6 p-5 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
            <label className="block font-semibold text-base text-slate-900 dark:text-white mb-1" htmlFor="qrUrl">
              {t('systemSettings.qrUrl')}
            </label>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              {t('systemSettings.qrUrlHint')}
            </p>
            <input
              type="url"
              id="qrUrl"
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-base transition-colors focus:outline-none focus:border-gold"
              value={formData.qrUrl}
              onChange={(e) => handleInputChange('qrUrl', e.target.value)}
              placeholder={t('systemSettings.qrUrlPlaceholder')}
            />
          </div>
        </section>

        {/* Save Button */}
        <div className="flex items-center gap-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex-wrap">
          <button
            className={`px-6 py-3 rounded-lg text-base font-semibold transition-all ${
              hasChanges && !saving
                ? 'bg-gold text-slate-900 hover:bg-gold-dark hover:-translate-y-0.5 cursor-pointer'
                : 'bg-slate-500 text-white opacity-60 cursor-not-allowed'
            }`}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? t('systemSettings.saving') : t('systemSettings.saveBtn')}
          </button>

          {hasChanges && (
            <span className="text-gold text-sm italic">{t('systemSettings.unsaved')}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default SystemSettings;
